import { Howler } from 'howler';
import type { TourRecordingConfig, RecordingStatus, RecordingResult } from './tour-recording-types';
import { isCorruptOrGradientCanvas } from '../transitions/frame-validator';
import { resolveOptimalRecordingDimensions, drawActiveInteractiveModals, drawAiInteractionHud } from './tour-recorder-modal-drawer';

/**
 * Cấu trúc quản lý liên kết audio node (ngăn V8 thu gom rác MediaStreamAudioSourceNode)
 */
interface ActiveAudioBridge {
    node: AudioNode;
    foreignDest?: MediaStreamAudioDestinationNode;
    localSource?: MediaStreamAudioSourceNode;
}

/**
 * Tìm KrPano WebGL canvas trong DOM
 */
function findKrpanoCanvas(): HTMLCanvasElement | null {
    if (typeof document === 'undefined') return null;

    // Tìm trong container phổ biến, bỏ qua các canvas overlay chuyển cảnh
    const container = document.getElementById('panorama') || document.getElementById('pano');
    if (container) {
        if (container instanceof HTMLCanvasElement && !container.hasAttribute('data-transition-overlay')) return container;
        const canvas = container.querySelector('canvas:not([data-transition-overlay="true"])') || container.querySelector('canvas');
        if (canvas) return canvas as HTMLCanvasElement;
    }

    // Fallback tìm canvas có webgl context kích thước lớn
    const all = document.querySelectorAll<HTMLCanvasElement>('canvas:not([data-transition-overlay="true"])');
    for (let i = 0; i < all.length; i++) {
        const c = all[i];
        if (c.width > 200 && c.height > 200 && !c.closest('.offcanvas-chatbot')) {
            return c as HTMLCanvasElement;
        }
    }
    return null;
}

/**
 * Tìm 3D Avatar canvas trong DOM (nếu trợ lý AI đang hoạt động)
 */
function findAvatarCanvas(): HTMLCanvasElement | null {
    if (typeof document === 'undefined') return null;
    const chatbot = document.querySelector('.offcanvas-chatbot');
    if (!chatbot) return null;
    return chatbot.querySelector('canvas');
}

/**
 * Chọn MimeType tương thích nhất với browser (Ưu tiên MP4 để sử dụng lâu dài và gửi khách hàng)
 */
function getBestSupportedMimeType(): string {
    if (typeof MediaRecorder === 'undefined') return '';
    const types = [
        'video/mp4;codecs=avc1,mp4a.40.2',
        'video/mp4;codecs=avc1,opus',
        'video/mp4;codecs=avc1',
        'video/mp4',
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm'
    ];
    for (const t of types) {
        if (MediaRecorder.isTypeSupported(t)) return t;
    }
    return '';
}

export class TourRecorder {
    private static instance: TourRecorder;

    private config: TourRecordingConfig = {};
    private status: RecordingStatus = 'idle';
    private offscreenCanvas: HTMLCanvasElement | null = null;
    private ctx: CanvasRenderingContext2D | null = null;
    private mediaRecorder: MediaRecorder | null = null;
    private recordedChunks: Blob[] = [];

    private audioContext: AudioContext | null = null;
    private audioDestination: MediaStreamAudioDestinationNode | null = null;
    private externalAudioNodes: Set<AudioNode> = new Set();
    private externalMediaElements: Set<HTMLMediaElement> = new Set();
    private externalMediaStreams: Set<MediaStream> = new Set();
    private streamSourceNodes: Map<MediaStream, MediaStreamAudioSourceNode> = new Map();
    private activeMicTrackIds: Set<string> = new Set();
    private activeBridges: Map<AudioNode, ActiveAudioBridge> = new Map();
    private keepAliveOscillator: OscillatorNode | null = null;
    private cachedKrpanoCanvas: HTMLCanvasElement | null = null;
    private cachedAvatarCanvas: HTMLCanvasElement | null = null;
    private hotspotIconCache = new Map<string, HTMLImageElement | null>();
    private svgIconCache = new Map<string, HTMLImageElement | null>();
    private assetCorsImageCache = new Map<string, HTMLImageElement | null>();
    private cleanSourceCache = new WeakMap<object, boolean>();
    private testProbeCanvas: HTMLCanvasElement | null = null;
    private testProbeCtx: CanvasRenderingContext2D | null = null;
    private lastFrameRenderTime: number = 0;
    private fallbackHeartbeatTimer: any = null;
    private visibilityChangeHandler: (() => void) | null = null;

    private rafId: number | null = null;
    private timerInterval: NodeJS.Timeout | null = null;
    private startTime: number = 0;
    private elapsedTime: number = 0;

    private constructor() {}

    public static getInstance(): TourRecorder {
        if (typeof window !== 'undefined') {
            const win = window as any;
            if (!win.__TOUR_RECORDER_INSTANCE__) {
                win.__TOUR_RECORDER_INSTANCE__ = TourRecorder.instance || new TourRecorder();
            }
            TourRecorder.instance = win.__TOUR_RECORDER_INSTANCE__;
            return win.__TOUR_RECORDER_INSTANCE__;
        }
        if (!TourRecorder.instance) {
            TourRecorder.instance = new TourRecorder();
        }
        return TourRecorder.instance;
    }

    public isSupported(): boolean {
        if (typeof window === 'undefined') return false;
        const hasMediaRecorder = typeof MediaRecorder !== 'undefined';
        const hasCanvasCapture = typeof HTMLCanvasElement !== 'undefined' &&
            typeof HTMLCanvasElement.prototype.captureStream === 'function';
        return hasMediaRecorder && hasCanvasCapture;
    }

    public getStatus(): RecordingStatus {
        return this.status;
    }

    public isRecording(): boolean {
        return this.status === 'recording';
    }

    public getElapsedTime(): number {
        return this.elapsedTime;
    }

    /**
     * Cung cấp hoặc tái sử dụng AudioContext toàn cục (ưu tiên từ Howler hoặc window)
     */
    public getOrCreateAudioContext(): AudioContext | null {
        if (typeof window === 'undefined') return null;
        if (!this.audioContext) {
            const win = window as any;
            if (typeof Howler !== 'undefined' && (Howler as any).ctx) {
                this.audioContext = (Howler as any).ctx;
            } else if (win.__GLOBAL_AUDIO_CONTEXT__ && win.__GLOBAL_AUDIO_CONTEXT__.state !== 'closed') {
                this.audioContext = win.__GLOBAL_AUDIO_CONTEXT__;
            } else {
                const AudioContextClass = window.AudioContext || win.webkitAudioContext;
                if (AudioContextClass) {
                    this.audioContext = new AudioContextClass();
                    win.__GLOBAL_AUDIO_CONTEXT__ = this.audioContext;
                }
            }
        }
        return this.audioContext;
    }

    /**
     * Đăng ký nguồn audio bên ngoài (ví dụ: giọng AI từ Gemini Live AudioStreamer, micro người dùng)
     */
    public registerAudioSource(node: AudioNode): void {
        if (!node) return;
        this.externalAudioNodes.add(node);
        if (this.isRecording() && this.audioDestination && this.audioContext) {
            this.bridgeAudioNode(node);
        }
    }

    public unregisterAudioSource(node: AudioNode): void {
        this.externalAudioNodes.delete(node);
        const incomingStream = (node as any)?.mediaStream as MediaStream | undefined;
        const micTrack = incomingStream?.getAudioTracks?.()?.[0];
        if (micTrack?.id) {
            this.activeMicTrackIds.delete(micTrack.id);
        }
        const bridge = this.activeBridges.get(node);
        if (bridge) {
            try {
                bridge.localSource?.disconnect();
                bridge.foreignDest?.disconnect();
            } catch {}
            this.activeBridges.delete(node);
        }
    }

    /**
     * Đăng ký thẻ HTML5 Media Element (<audio> hoặc <video>) để streaming trực tiếp vào video recording
     */
    public registerMediaElement(element: HTMLMediaElement): void {
        if (!element) return;
        this.externalMediaElements.add(element);
        if (this.isRecording() && this.audioDestination && this.audioContext) {
            this.bridgeMediaElement(element);
        }
    }

    public registerExternalMediaStream(stream: MediaStream): void {
        if (!stream) return;
        this.externalMediaStreams.add(stream);
        if (this.isRecording() && this.audioDestination && this.audioContext) {
            this.bridgeExternalMediaStream(stream);
        }
    }

    public unregisterExternalMediaStream(stream: MediaStream): void {
        this.externalMediaStreams.delete(stream);
        const track = stream?.getAudioTracks?.()?.[0];
        if (track?.id) {
            this.activeMicTrackIds.delete(track.id);
        }
        const node = this.streamSourceNodes.get(stream);
        if (node) {
            try { node.disconnect(); } catch {}
            this.streamSourceNodes.delete(stream);
        }
    }

    private bridgeExternalMediaStream(stream: MediaStream): void {
        if (!this.audioDestination || !this.audioContext || !stream) return;
        if (this.streamSourceNodes.has(stream)) return;
        try {
            if (typeof stream.getAudioTracks === 'function' && stream.getAudioTracks().length > 0) {
                const track = stream.getAudioTracks()[0];
                if (track?.id && this.activeMicTrackIds.has(track.id)) {
                    return;
                }
                if (track?.id) {
                    this.activeMicTrackIds.add(track.id);
                }
                const sourceNode = this.audioContext.createMediaStreamSource(stream);
                sourceNode.connect(this.audioDestination);
                this.streamSourceNodes.set(stream, sourceNode);
            }
        } catch (err) {
            console.warn('[TourRecorder] Lỗi bridge MediaStream:', err);
        }
    }

    private _unusedMarkerForRegisterMediaElement(_element: HTMLMediaElement): void { return; }
    private _unusedBody(element: HTMLMediaElement): void {
        if (!element) return;
        this.externalMediaElements.add(element);
        if (this.isRecording() && this.audioDestination && this.audioContext) {
            this.bridgeMediaElement(element);
        }
    }

    public unregisterMediaElement(element: HTMLMediaElement): void {
        this.externalMediaElements.delete(element);
        const streamSourceNode = (element as any).__mediaStreamSourceNode as AudioNode | undefined;
        if (streamSourceNode) {
            this.unregisterAudioSource(streamSourceNode);
        }
        const elementSourceNode = (element as any).__mediaElementSourceNode as AudioNode | undefined;
        if (elementSourceNode) {
            this.unregisterAudioSource(elementSourceNode);
        }
    }

    /**
     * Kết nối an toàn HTMLMediaElement vào AudioDestination của MediaRecorder:
     * - Ưu tiên captureStream() để lấy luồng âm thanh thu vào video mà HOÀN TOÀN KHÔNG ngắt
     *   kết nối âm thanh ra loa thiết bị người dùng (không bị mất tiếng / silent).
     * - Fallback sang createMediaElementSource() nếu trình duyệt không hỗ trợ captureStream().
     */
    private bridgeMediaElement(element: HTMLMediaElement): void {
        if (!this.audioDestination || !this.audioContext || !element) return;
        try {
            // 1. Ưu tiên captureStream()
            const captureFn = (element as any).captureStream || (element as any).mozCaptureStream;
            if (typeof captureFn === 'function') {
                let stream: MediaStream | undefined = (element as any).__capturedMediaStream;
                if (!stream) {
                    try {
                        stream = captureFn.call(element);
                        (element as any).__capturedMediaStream = stream;
                    } catch {}
                }
                if (stream && typeof stream.getAudioTracks === 'function' && stream.getAudioTracks().length > 0) {
                    let sourceNode: AudioNode | undefined = (element as any).__mediaStreamSourceNode;
                    if (!sourceNode && typeof this.audioContext.createMediaStreamSource === 'function') {
                        sourceNode = this.audioContext.createMediaStreamSource(stream);
                        (element as any).__mediaStreamSourceNode = sourceNode;
                    }
                    if (sourceNode && !this.activeBridges.has(sourceNode)) {
                        this.bridgeAudioNode(sourceNode);
                    }
                    return;
                }
            }

            // 2. Fallback createMediaElementSource()
            let sourceNode: MediaElementAudioSourceNode | undefined = (element as any).__mediaElementSourceNode;
            if (!sourceNode && typeof this.audioContext.createMediaElementSource === 'function') {
                sourceNode = this.audioContext.createMediaElementSource(element);
                (element as any).__mediaElementSourceNode = sourceNode;
                // Đã loại bỏ sourceNode.connect(destination) ra loa để triệt tiêu acoustic echo lặp âm
            }

            if (sourceNode && !this.activeBridges.has(sourceNode)) {
                this.bridgeAudioNode(sourceNode);
            }
        } catch (e) {
            console.warn('[TourRecorder] Lỗi bridge HTMLMediaElement:', e);
        }
    }


    /**
     * Kết nối âm thanh Howler (AutoTour voice narration & background music)
     */
    private bridgeHowlerAudio(): void {
        if (typeof Howler === 'undefined') return;
        try {
            const howlerCtx = (Howler as any).ctx;
            if (howlerCtx && howlerCtx.state === 'suspended') {
                howlerCtx.resume().catch(() => {});
            }
            const masterGain = (Howler as any).masterGain;
            if (masterGain && !this.activeBridges.has(masterGain)) {
                this.bridgeAudioNode(masterGain);
            }
        } catch (e) {
            console.warn('[TourRecorder] Lỗi bridge Howler audio:', e);
        }
    }

    /**
     * Đồng bộ ngay các nguồn âm thanh (Howler + External Nodes)
     */
    public syncAudioSources(): void {
        if (this.status !== 'recording' || !this.audioDestination || !this.audioContext) return;
        this.bridgeHowlerAudio();
        for (const node of this.externalAudioNodes) {
            if (!this.activeBridges.has(node)) {
                this.bridgeAudioNode(node);
            }
        }
        for (const el of this.externalMediaElements) {
            this.bridgeMediaElement(el);
        }
    }

    /**
     * Kết nối an toàn AudioNode vào destination, hỗ trợ bridging giữa 2 AudioContext khác nhau
     * Giữ tham chiếu persistent trong activeBridges để Chromium V8 không thu gom rác (garbage collection)
     */
    private bridgeAudioNode(node: AudioNode): void {
        if (!this.audioDestination || !this.audioContext || !node) return;
        if (this.activeBridges.has(node)) return;

        const incomingStream = (node as any).mediaStream as MediaStream | undefined;
        const micTrack = incomingStream?.getAudioTracks?.()?.[0];
        if (micTrack?.id) {
            if (this.activeMicTrackIds.has(micTrack.id)) {
                return;
            }
            this.activeMicTrackIds.add(micTrack.id);
        }

        try {
            const foreignCtx = node.context as AudioContext;
            if (foreignCtx && foreignCtx.state === 'suspended') {
                foreignCtx.resume().catch(() => {});
            }

            if (!foreignCtx || foreignCtx === this.audioContext) {
                node.connect(this.audioDestination);
                this.activeBridges.set(node, { node });
            } else if (typeof (foreignCtx as any)?.createMediaStreamDestination === 'function') {
                // Cross-context bridging: context ngoại -> MediaStreamDestination -> MediaStreamSource -> destination nội
                const foreignDest = (foreignCtx as any).createMediaStreamDestination();
                node.connect(foreignDest);
                if (foreignDest.stream && typeof foreignDest.stream.getAudioTracks === 'function' && foreignDest.stream.getAudioTracks().length > 0) {
                    const localSource = this.audioContext.createMediaStreamSource(foreignDest.stream);
                    localSource.connect(this.audioDestination);
                    // Giữ tham chiếu persistent để V8 GC không hủy stream giữa chừng
                    this.activeBridges.set(node, { node, foreignDest, localSource });
                } else {
                    this.activeBridges.set(node, { node, foreignDest });
                }
            }
        } catch (err) {
            console.warn('[TourRecorder] Lỗi bridge audio node:', err);
        }
    }

    private updateStatus(newStatus: RecordingStatus): void {
        this.status = newStatus;
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('tour-recording-status-change', {
                detail: { status: newStatus, elapsedTime: this.elapsedTime, isRecording: newStatus === 'recording' }
            }));
        }
        if (this.config.onStatusChange) {
            this.config.onStatusChange(newStatus);
        }
    }

    /**
     * Bắt đầu ghi hình Tour
     */
    public async startRecording(config: TourRecordingConfig = {}): Promise<boolean> {
        if (this.status === 'recording') return true;
        if (!this.isSupported()) {
            config.onError?.('Trình duyệt của bạn không hỗ trợ tính năng ghi hình tour');
            return false;
        }

        this.config = config;
        this.recordedChunks = [];
        this.elapsedTime = 0;

        const optimal = resolveOptimalRecordingDimensions(config.width, config.height, config.fps, config.videoBitsPerSecond);
        const targetW = optimal.width;
        const targetH = optimal.height;
        const targetFps = optimal.fps;
        const targetBitrate = optimal.videoBitsPerSecond;



        // Đảm bảo âm thanh tour được bật (unmute) khi bắt đầu ghi hình
        // Ngoại lệ: KHÔNG bật lại nếu có cờ suppressSceneAudio, hoặc recordingType === 'ai_interaction', hoặc __aiAgentActive
        const shouldSuppressSceneAudio = Boolean(
            this.config.suppressSceneAudio ||
            this.config.recordingType === 'ai_interaction' ||
            (typeof window !== 'undefined' && ((window as any).__aiAgentActive || (window as any).isOpenChatBot))
        );
        if (!shouldSuppressSceneAudio && typeof window !== 'undefined' && window.isMuted && typeof window.toggleMute === 'function' && !(window as any).__isAiVirtualTourPlaying) {
            window.toggleMute(false);
        }

        // 1. Tạo Canvas Composite
        if (!this.offscreenCanvas) {
            this.offscreenCanvas = document.createElement('canvas');
        }
        this.offscreenCanvas.width = targetW;
        this.offscreenCanvas.height = targetH;
        this.ctx = this.offscreenCanvas.getContext('2d');
        if (!this.ctx) {
            config.onError?.('Không thể tạo 2D context cho canvas ghi hình');
            return false;
        }

        // Vẽ ngay 1 frame nền đầu tiên để canvas stream không bị rỗng
        this.ctx.fillStyle = '#0f172a';
        this.ctx.fillRect(0, 0, targetW, targetH);

        // 2. Cấu hình Audio Mixing
        try {
            // Ưu tiên tái sử dụng AudioContext từ Howler hoặc window để đồng bộ với âm thanh autotour
            this.getOrCreateAudioContext();

            if (this.audioContext) {
                // Bảo đảm AudioContext ở trạng thái 'running'
                if (this.audioContext.state === 'suspended') {
                    await this.audioContext.resume().catch(() => {});
                }

                this.audioDestination = this.audioContext.createMediaStreamDestination();

                // Kết nối Howler master audio (AutoTour voice narration & background music)
                this.bridgeHowlerAudio();

                // Kết nối các nguồn audio bên ngoài đã đăng ký (giọng AI agent, mic...)
                for (const node of this.externalAudioNodes) {
                    this.bridgeAudioNode(node);
                }

                // Kết nối các thẻ HTML5 media elements đã đăng ký (AutoTour streaming audio...)
                for (const el of this.externalMediaElements) {
                    this.bridgeMediaElement(el);
                }

                // Kết nối các MediaStream bên ngoài (ví dụ Micro người dùng khi quay tương tác AI trên Mobile)
                for (const stream of this.externalMediaStreams) {
                    this.bridgeExternalMediaStream(stream);
                }

                // Tự động kết nối giọng nói AI (Gemini Live / OpenAI AudioStreamer) nếu đang hoạt động
                const aiGainNode = (window as any)?.__geminiAudioStreamer?.gainNode as AudioNode | undefined;
                if (aiGainNode) {
                    this.bridgeAudioNode(aiGainNode);
                }

                // Bơm xung âm thanh im lặng (silent keep-alive node)
                // để đảm bảo AudioContext luôn phát timestamp liên tục cho MediaRecorder của Chromium
                try {
                    const osc = this.audioContext.createOscillator();
                    const silentGain = this.audioContext.createGain();
                    silentGain.gain.value = 0.00001; // Âm lượng 0 gần như tuyệt đối (silent)
                    osc.connect(silentGain);
                    silentGain.connect(this.audioDestination);
                    osc.start();
                    this.keepAliveOscillator = osc;
                } catch {
                    // Ignore oscillator error in test or mock environment
                }
            }
        } catch (err) {
            console.warn('[TourRecorder] Không thể khởi tạo Audio Context:', err);
        }

        // 3. Khởi tạo Stream và MediaRecorder
        try {
            const canvasStream = this.offscreenCanvas.captureStream(targetFps);
            const combinedTracks: MediaStreamTrack[] = [...canvasStream.getVideoTracks()];

            if (this.audioDestination && this.audioDestination.stream.getAudioTracks().length > 0) {
                combinedTracks.push(...this.audioDestination.stream.getAudioTracks());
            }

            const MediaStreamClass = (typeof window !== 'undefined' && (window as any).MediaStream) 
                ? (window as any).MediaStream 
                : (typeof MediaStream !== 'undefined' ? MediaStream : null);
            const combinedStream = MediaStreamClass ? new MediaStreamClass(combinedTracks) : canvasStream;
            const mimeType = getBestSupportedMimeType();
            const bitrate = targetBitrate;

            this.mediaRecorder = new MediaRecorder(combinedStream, {
                mimeType,
                videoBitsPerSecond: bitrate
            });

            this.mediaRecorder.ondataavailable = (e: BlobEvent) => {
                if (e.data && e.data.size > 0) {
                    this.recordedChunks.push(e.data);
                }
            };

            this.mediaRecorder.onerror = (e) => {
                console.warn('[TourRecorder] MediaRecorder notice:', e);
            };
            this.mediaRecorder.start(1000); // Lưu chunk mỗi 1 giây
        } catch (err: any) {
            console.error('[TourRecorder] Không thể khởi tạo MediaRecorder:', err);
            config.onError?.(err?.message || 'Không thể bắt đầu ghi hình');
            this.cleanup();
            return false;
        }

        // 4. Bắt đầu vòng lặp render và đồng hồ đếm thời gian
        this.startTime = Date.now();
        this.updateStatus('recording');
        this.startRenderLoop(targetW, targetH);

        // 4b. Duy trì ghi hình ổn định trên Mobile khi có Popup / mất focus / đổi tab
        if (typeof document !== 'undefined') {
            this.visibilityChangeHandler = () => {
                if (this.status !== 'recording') return;
                if (document.visibilityState === 'visible') {
                    if (this.audioContext && this.audioContext.state === 'suspended') {
                        this.audioContext.resume().catch(() => {});
                    }
                }
            };
            document.addEventListener('visibilitychange', this.visibilityChangeHandler);
        }

        // Bổ sung Heartbeat dự phòng: nếu RAF bị Mobile OS pause do popup/background (>150ms),
        // fallback interval vẫn định kỳ duy trì frame để captureStream không bị ngắt luồng
        this.fallbackHeartbeatTimer = setInterval(() => {
            if (this.status === 'recording' && this.ctx && this.offscreenCanvas) {
                const now = Date.now();
                if (now - this.lastFrameRenderTime > 150) {
                    this.lastFrameRenderTime = now;
                    try {
                        if (this.cachedKrpanoCanvas && this.cachedKrpanoCanvas.width > 0) {
                            this.ctx.drawImage(this.cachedKrpanoCanvas, 0, 0, targetW, targetH);
                        }
                    } catch {}
                }
            }
        }, 100);

        this.timerInterval = setInterval(() => {
            if (this.status === 'recording') {
                this.elapsedTime = Math.floor((Date.now() - this.startTime) / 1000);
                // Liên tục kiểm tra và đồng bộ Howler audio nếu được khởi tạo sau khi bắt đầu ghi
                this.bridgeHowlerAudio();
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('tour-recording-time-update', {
                        detail: { elapsedTime: this.elapsedTime, status: this.status }
                    }));
                }
                if (this.config.onTimeUpdate) {
                    this.config.onTimeUpdate(this.elapsedTime);
                }
            }
        }, 1000);

        return true;
    }

    /**
     * Vòng lặp vẽ liên tục 360° Panorama + Avatar PiP (nếu có)
     * Bảo toàn frame trước đó, tuyệt đối không vẽ nền đen đè lên khi camera dừng hoặc chuyển cảnh
     */
    private startRenderLoop(w: number, h: number): void {
        const render = () => {
            if (this.status !== 'recording' || !this.ctx || !this.offscreenCanvas) return;

            // 1. Thúc đẩy KrPano cập nhật frame để WebGL drawing buffer luôn mới (đặc biệt khi camera đứng yên)
            if (typeof window !== 'undefined' && (window as any).krpano && typeof (window as any).krpano.call === 'function') {
                try {
                    (window as any).krpano.call('updatescreen();');
                } catch {
                    // Ignore error if krpano busy
                }
            }

            // 2. Lấy canvas KrPano 360 (có cache DOM query để tối ưu hiệu năng 60fps)
            let krpanoCanvas = this.cachedKrpanoCanvas;
            if (!krpanoCanvas || !krpanoCanvas.isConnected) {
                krpanoCanvas = findKrpanoCanvas();
                this.cachedKrpanoCanvas = krpanoCanvas;
            }

            if (krpanoCanvas && krpanoCanvas.width > 0 && krpanoCanvas.height > 0) {
                try {
                    const srcW = krpanoCanvas.width;
                    const srcH = krpanoCanvas.height;
                    // Thu phóng giữ nguyên tỉ lệ gốc (cover mode), chống méo hình khi màn hình không phải chuẩn 16:9
                    const scale = Math.max(w / srcW, h / srcH);
                    const drawW = srcW * scale;
                    const drawH = srcH * scale;
                    const offsetX = (w - drawW) / 2;
                    const offsetY = (h - drawH) / 2;

                    // Vẽ trực tiếp canvas KrPano 360 thời gian thực (60fps), không giữ cached frame cũ để chống giật/đứng hình khi chuyển cảnh
                    this.ctx.drawImage(krpanoCanvas, offsetX, offsetY, drawW, drawH);

                    // 2b. Vẽ các canvas overlay chuyển cảnh (TransitionEngine / staticHoldCanvas: SLIDE, WIPE, CUBEFADE, 29 biến thể)
                    const transitionCanvases = this.findTransitionOverlayCanvases(krpanoCanvas);
                    for (const transCanvas of transitionCanvases) {
                        try {
                            const tSrcW = transCanvas.width;
                            const tSrcH = transCanvas.height;
                            const tScale = Math.max(w / tSrcW, h / tSrcH);
                            const tDrawW = tSrcW * tScale;
                            const tDrawH = tSrcH * tScale;
                            const tOffsetX = (w - tDrawW) / 2;
                            const tOffsetY = (h - tDrawH) / 2;
                            this.ctx.drawImage(transCanvas, tOffsetX, tOffsetY, tDrawW, tDrawH);
                        } catch {
                            // Bỏ qua nếu canvas overlay đang giải phóng hoặc cross-origin
                        }
                    }

                    // 3a. Vẽ icon hotspot điều hướng (sprite DOM, nhiều frame) vì không nằm trong WebGL canvas
                    if (transitionCanvases.length === 0) {
                        this.drawHotspotIcons(krpanoCanvas, offsetX, offsetY, drawW, drawH);
                    }

                    // 3. Vẽ nhãn văn bản Hotspot (Callout và Titles) lên recording canvas
                    if (transitionCanvases.length === 0) {
                        this.drawHotspotOverlays(w, h, krpanoCanvas, offsetX, offsetY, drawW, drawH);
                    }

                    // 4. Vẽ card xem trước tài nguyên (TourAssetCornerPreview) kèm hiệu ứng animation
                    if (transitionCanvases.length === 0) {
                        this.drawTourAssetPreview(w, h, krpanoCanvas);
                    }
                } catch {
                    // Tránh crash nếu canvas bị cross-origin
                }
            } else {
                // Nếu krpanoCanvas tạm thời đang nạp lại trong tick này nhưng có canvas overlay chuyển cảnh
                const transitionCanvases = this.findTransitionOverlayCanvases();
                for (const transCanvas of transitionCanvases) {
                    try {
                        const tSrcW = transCanvas.width;
                        const tSrcH = transCanvas.height;
                        const tScale = Math.max(w / tSrcW, h / tSrcH);
                        const tDrawW = tSrcW * tScale;
                        const tDrawH = tSrcH * tScale;
                        const tOffsetX = (w - tDrawW) / 2;
                        const tOffsetY = (h - tDrawH) / 2;
                        this.ctx.drawImage(transCanvas, tOffsetX, tOffsetY, tDrawW, tDrawH);
                    } catch {
                        // Bỏ qua nếu canvas overlay đang giải phóng hoặc cross-origin
                    }
                }
            }
            // LƯU Ý QUAN TRỌNG: Tuyệt đối KHÔNG fillRect màu đen (#0f172a) khi không vẽ được KrPano.
            // Nếu KrPano đang chuyển cảnh hoặc không vẽ kịp trong 1 tick, giữ nguyên frame hợp lệ trước đó
            // để video luôn liên tục, mượt mà và không bao giờ bị chớp đen!

            // 2.5. Vẽ các Modal tương tác AI đang mở toàn màn hình (ImageModal / VideoModal / FacilityDemoOverlay)
            drawActiveInteractiveModals(
                this.ctx,
                w,
                h,
                (src) => this.isCleanImageSource(src),
                (url) => this.getCorsSafeAssetImage(url)
            );

            // 3. Vẽ 3D Avatar (Hiển thị chân thực, trong suốt, không có khung viền đen mờ bao quanh)
            let avatarCanvas = this.cachedAvatarCanvas;
            if (!avatarCanvas || !avatarCanvas.isConnected) {
                avatarCanvas = findAvatarCanvas();
                this.cachedAvatarCanvas = avatarCanvas;
            }
            if (avatarCanvas && avatarCanvas.width > 0 && avatarCanvas.height > 0) {
                const scaleRatio = w / 1920;
                let drawX: number;
                let drawY: number;
                let drawW: number;
                let drawH: number;

                // Tự động tính toán vị trí và kích thước đồng bộ theo vị trí thực tế trên màn hình
                const kRect = krpanoCanvas ? krpanoCanvas.getBoundingClientRect() : null;
                const aRect = avatarCanvas.getBoundingClientRect();

                if (kRect && kRect.width > 0 && kRect.height > 0 && aRect.width > 0 && aRect.height > 0) {
                    const scaleX = w / kRect.width;
                    const scaleY = h / kRect.height;
                    drawX = Math.round((aRect.left - kRect.left) * scaleX);
                    drawY = Math.round((aRect.top - kRect.top) * scaleY);
                    drawW = Math.round(aRect.width * scaleX);
                    drawH = Math.round(aRect.height * scaleY);
                } else {
                    // Fallback theo tỉ lệ chuẩn khi không đo được DOM
                    drawW = Math.round(380 * scaleRatio);
                    drawH = Math.round(520 * scaleRatio);
                    drawX = w - drawW - Math.round(40 * scaleRatio);
                    drawY = h - drawH - Math.round(40 * scaleRatio);
                }

                try {
                    // Vẽ trực tiếp canvas 3D avatar với nền trong suốt (alpha: true) lên panorama
                    // Tuyệt đối KHÔNG fillRect màu đen/mờ và KHÔNG vẽ viền chữ nhật bao xung quanh
                    this.ctx.drawImage(avatarCanvas, drawX, drawY, drawW, drawH);
                } catch {
                    // Ignore canvas draw error
                }
            }

            this.rafId = requestAnimationFrame(render);
        };

        this.rafId = requestAnimationFrame(render);
    }

    /**
     * Tìm các canvas overlay chuyển cảnh đang hiển thị trên viewer (nếu có)
     * Áp dụng cho: BLEND, SLIDE, WIPE, CUBEFADE, và 29 biến thể chuyên sâu của TransitionEngine
     */
    private findTransitionOverlayCanvases(_krpanoCanvas?: HTMLCanvasElement | null): HTMLCanvasElement[] {
        if (typeof document === 'undefined') return [];

        // Tìm toàn cục trên DOM vì canvas overlay được gắn vào viewer container (#panorama/#pano)
        // chứ không nằm bên trong div con nội bộ của KrPano
        const overlays = document.querySelectorAll<HTMLCanvasElement>('canvas[data-transition-overlay="true"]');
        if (!overlays || overlays.length === 0) return [];

        const list = Array.from(overlays).filter(c => c.width > 0 && c.height > 0 && c.isConnected && !isCorruptOrGradientCanvas(c));
        if (list.length <= 1) return list;

        // Nếu có nhiều canvas mang data-transition-overlay (ví dụ: staticHoldCanvas và engine.canvas),
        // chỉ trả về canvas trên cùng (top-most / phần tử cuối cùng trong DOM) để tránh vẽ đè chập chờn
        return [list[list.length - 1]];
    }

    private getHotspotIconImage(url: string): HTMLImageElement | null {
        if (!url) return null;
        if (this.hotspotIconCache.has(url)) {
            const cached = this.hotspotIconCache.get(url) ?? null;
            if (cached && cached.complete && cached.naturalWidth > 0) {
                return this.isCleanImageSource(cached) ? cached : null;
            }
            return null;
        }

        const img = new Image();
        img.crossOrigin = 'anonymous';

        // Nếu là ảnh từ CMC S3 hoặc external CDN, ưu tiên định tuyến qua same-origin proxy
        let targetSrc = url;
        if (
            typeof window !== 'undefined' &&
            window.location?.origin &&
            (url.includes('s3.hcm-1.cloud.cmctelecom.vn') || url.startsWith('http://') || url.startsWith('https://')) &&
            !url.startsWith(window.location.origin) &&
            !url.includes('/api/proxy-image')
        ) {
            targetSrc = `/api/proxy-image?url=${encodeURIComponent(url)}`;
        } else if (!targetSrc.includes('?') && !targetSrc.startsWith('data:')) {
            targetSrc = `${targetSrc}?_cors=1`;
        }

        img.onload = () => {
            if (this.isCleanImageSource(img)) {
                this.hotspotIconCache.set(url, img);
            } else {
                this.hotspotIconCache.set(url, null);
            }
        };

        img.onerror = () => {
            if (targetSrc.includes('/api/proxy-image')) {
                const fallbackImg = new Image();
                fallbackImg.crossOrigin = 'anonymous';
                fallbackImg.onload = () => {
                    if (this.isCleanImageSource(fallbackImg)) {
                        this.hotspotIconCache.set(url, fallbackImg);
                    } else {
                        this.hotspotIconCache.set(url, null);
                    }
                };
                fallbackImg.onerror = () => this.hotspotIconCache.set(url, null);
                fallbackImg.src = url.includes('?') ? `${url}&_cors=1` : `${url}?_cors=1`;
                this.hotspotIconCache.set(url, fallbackImg);
            } else {
                this.hotspotIconCache.set(url, null);
            }
        };

        img.src = targetSrc;
        this.hotspotIconCache.set(url, img);
        return null;
    }

    private getSvgIconImage(svgMarkup: string): HTMLImageElement | null {
        if (!svgMarkup) return null;
        if (this.svgIconCache.has(svgMarkup)) {
            const cached = this.svgIconCache.get(svgMarkup) ?? null;
            return cached && cached.complete && cached.naturalWidth > 0 ? cached : null;
        }

        let safeSvg = svgMarkup;
        if (!safeSvg.includes('width=') && !safeSvg.includes('width =')) {
            safeSvg = safeSvg.replace(/<svg\b/i, '<svg width="60" height="60" ');
        }

        const img = new Image();
        img.onload = () => {
            if (this.isCleanImageSource(img)) {
                this.svgIconCache.set(svgMarkup, img);
            } else {
                this.svgIconCache.set(svgMarkup, null);
            }
        };
        img.onerror = () => {
            this.svgIconCache.set(svgMarkup, null);
        };
        img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(safeSvg)}`;
        this.svgIconCache.set(svgMarkup, img);
        return null;
    }

    /**
     * Vẽ icon hotspot điều hướng (sprite DOM và SVG icons) lên recording canvas.
     * Hỗ trợ:
     * - Các hotspot điều hướng sàn (floor distorted: distorted=true / rx=-60)
     * - Các hotspot animated sprite sheets nhiều frame (crop frame: arrowDownNew, arrowUp, arrowLeft, arrowRight, hotspot_ani_white...)
     * - Các hotspot dạng SVG icon picker (arrow-up, arrow-down, arrow-left, arrow-right...)
     */
    private drawHotspotIcons(
        krpanoCanvas: HTMLCanvasElement,
        offsetX: number,
        offsetY: number,
        drawW: number,
        drawH: number
    ): void {
        if (!this.ctx || typeof window === 'undefined') return;
        const krpano = (window as any).krpano;
        if (!krpano || typeof krpano.get !== 'function') return;

        const kRect = krpanoCanvas.getBoundingClientRect();
        if (kRect.width <= 0 || kRect.height <= 0) return;
        const scaleX = drawW / kRect.width;
        const scaleY = drawH / kRect.height;

        try {
            const count = Number(krpano.get('hotspot.count') || 0);
            for (let i = 0; i < count; i++) {
                const name = krpano.get(`hotspot[${i}].name`);
                if (!name || typeof name !== 'string') continue;
                // Bỏ qua các text overlay và hotspot vô hình (được xử lý riêng ở drawHotspotOverlays)
                if (name.endsWith('_callout_text') || name.endsWith('_invisible')) continue;

                const visible = krpano.get(`hotspot[${name}].visible`);
                const alpha = Number(krpano.get(`hotspot[${name}].alpha`) ?? 1);
                if (visible === false || visible === 'false' || alpha <= 0.05) continue;

                // 1. Kiểm tra trường nhìn góc cầu (Spherical FOV Check):
                // Hotspot phải nằm trong tầm nhìn phía trước camera (FOV).
                // Loại bỏ triệt để các hotspot nằm sau lưng (deltaH > 70) hoặc lệch góc trên/dưới quá lớn (deltaV > 65).
                // Vì trong CSS 3D, Chromium chiếu các điểm sau lưng (Z < 0) bị nghịch đảo ngược lên trời hoặc kéo giãn sọc trắng.
                const ath = Number(krpano.get(`hotspot[${name}].ath`));
                const atv = Number(krpano.get(`hotspot[${name}].atv`));
                if (Number.isFinite(ath)) {
                    const hlookat = Number(krpano.get('view.hlookat') || 0);
                    const deltaH = Math.abs(((ath - hlookat + 540) % 360) - 180);
                    if (deltaH > 70) continue;
                }
                if (Number.isFinite(atv)) {
                    const vlookat = Number(krpano.get('view.vlookat') || 0);
                    const deltaV = Math.abs(atv - vlookat);
                    if (deltaV > 65) continue;
                }

                // 2. Kiểm tra tọa độ màn hình từ KrPano (nếu API spheretoscreen có sẵn):
                if (typeof krpano.spheretoscreen === 'function' && Number.isFinite(ath) && Number.isFinite(atv)) {
                    try {
                        const sp = krpano.spheretoscreen(ath, atv);
                        if (sp && Number.isFinite(sp.x) && Number.isFinite(sp.y)) {
                            if (sp.x < -60 || sp.x > kRect.width + 60 || sp.y < -60 || sp.y > kRect.height + 60) {
                                continue;
                            }
                        }
                    } catch {}
                }

                const sprite = krpano.get(`hotspot[${name}].sprite`) as HTMLElement | null;
                if (!sprite || !sprite.isConnected) continue;

                if (sprite.style.display === 'none' || sprite.style.visibility === 'hidden') continue;

                const sRect = sprite.getBoundingClientRect();
                if (sRect.width <= 0 || sRect.height <= 0) continue;

                // 3. Kiểm tra kích thước và tỉ lệ hợp lý của icon (chống sọc trắng):
                // Loại bỏ các bounding box bị kéo giãn bất thường do phép chiếu 3D ở đường chân trời
                if (sRect.width > kRect.width * 0.35 || sRect.height > kRect.height * 0.35) continue;
                const aspect = sRect.width / sRect.height;
                if (aspect > 4.5 || aspect < 0.2) continue;

                // 4. Kiểm tra độ cao mặt sàn đối với hotspot điều hướng sàn (chống bay lên trời):
                // Hotspot nằm sàn (distorted == true hoặc rx != 0 hoặc atv >= 0) khi camera nhìn ngang (vlookat <= 15)
                // tuyệt đối không thể xuất hiện trên bầu trời (sRect.top < 15% chiều cao màn hình)
                const rx = Number(krpano.get(`hotspot[${name}].rx`) || 0);
                const distorted = krpano.get(`hotspot[${name}].distorted`);
                const isDistorted = distorted === true || distorted === 'true' || rx !== 0;
                const vlookat = Number(krpano.get('view.vlookat') || 0);
                if (isDistorted && atv >= -5 && vlookat <= 15) {
                    if (sRect.top < kRect.top + kRect.height * 0.15) {
                        continue;
                    }
                }

                const centerX = sRect.left + sRect.width / 2;
                const centerY = sRect.top + sRect.height / 2;
                if (
                    centerX < kRect.left - sRect.width * 0.3 ||
                    centerX > kRect.right + sRect.width * 0.3 ||
                    centerY < kRect.top - sRect.height * 0.3 ||
                    centerY > kRect.bottom + sRect.height * 0.3
                ) {
                    continue;
                }
                const destX = offsetX + (sRect.left - kRect.left) * scaleX;
                const destY = offsetY + (sRect.top - kRect.top) * scaleY;
                const destW = sRect.width * scaleX;
                const destH = sRect.height * scaleY;

                // TH1: Hotspot điều hướng dạng SVG (SVG icon picker: arrow-up, arrow-down, arrow-left, arrow-right...)
                const svgEl = sprite.querySelector('svg');
                if (svgEl) {
                    const bgContainer = (svgEl.closest('div[style*="background-color"]') as HTMLElement) ||
                                       (svgEl.parentElement as HTMLElement) ||
                                       sprite;
                    const compStyle = window.getComputedStyle(bgContainer);
                    const bgColor = compStyle.backgroundColor;
                    const borderRadius = parseFloat(compStyle.borderRadius) || 8;

                    this.ctx.save();
                    this.ctx.globalAlpha = Math.min(1, Math.max(0, alpha));

                    // Vẽ khung nền bo góc của icon SVG nếu có
                    if (bgColor && bgColor !== 'transparent' && bgColor !== 'rgba(0, 0, 0, 0)') {
                        this.ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
                        this.ctx.shadowBlur = 8 * scaleX;
                        this.ctx.shadowOffsetY = 2 * scaleY;
                        this.ctx.fillStyle = bgColor;
                        if (typeof this.ctx.roundRect === 'function') {
                            this.ctx.beginPath();
                            this.ctx.roundRect(destX, destY, destW, destH, borderRadius * scaleX);
                            this.ctx.fill();
                        } else {
                            this.ctx.fillRect(destX, destY, destW, destH);
                        }
                    }

                    // Rasterize và vẽ icon SVG vào trong khung
                    const svgMarkup = new XMLSerializer().serializeToString(svgEl);
                    const svgImg = this.getSvgIconImage(svgMarkup);
                    if (svgImg) {
                        const padX = Math.max(2, Math.round(destW * 0.12));
                        const padY = Math.max(2, Math.round(destH * 0.12));
                        this.ctx.shadowColor = 'transparent';
                        this.ctx.drawImage(
                            svgImg,
                            destX + padX,
                            destY + padY,
                            Math.max(1, destW - 2 * padX),
                            Math.max(1, destH - 2 * padY)
                        );
                    }

                    this.ctx.restore();
                    continue;
                }

                // TH2: Hotspot điều hướng dạng ảnh sprite sheet động (arrow-up, arrowLeft, arrowRight, hotspot_ani_white...)
                const bg = window.getComputedStyle(sprite).backgroundImage || '';
                const bgMatch = bg.match(/url\((['"]?)(.*?)\1\)/);
                let url = bgMatch?.[2] || String(krpano.get(`hotspot[${name}].url`) || '');
                if (!url) continue;

                // Chuẩn hóa relative URL sang absolute URL nếu cần
                if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('data:')) {
                    try {
                        url = new URL(url, window.location.href).href;
                    } catch {
                        // Giữ nguyên url
                    }
                }

                const img = this.getHotspotIconImage(url);
                if (!img) continue;

                // Xác định frame cắt crop hiện tại của sprite sheet:
                // 1) Ưu tiên thuộc tính crop ("x|y|w|h") do KrPano cập nhật theo tick animation
                const cropParts = String(krpano.get(`hotspot[${name}].crop`) || '')
                    .split('|')
                    .map(Number);
                const hasCrop = cropParts.length === 4 && cropParts.every(Number.isFinite) && cropParts[2] > 0 && cropParts[3] > 0;

                let sx = 0;
                let sy = 0;
                let sw = img.naturalWidth;
                let sh = img.naturalHeight;

                if (hasCrop) {
                    [sx, sy, sw, sh] = cropParts;
                } else {
                    // 2) Tính toán từ framewidth, frameheight và frame hiện tại
                    const fw = Number(krpano.get(`hotspot[${name}].framewidth`) || 0);
                    const fh = Number(krpano.get(`hotspot[${name}].frameheight`) || 0);
                    const frame = Number(krpano.get(`hotspot[${name}].frame`) || 0);
                    if (fw > 0 && fh > 0) {
                        sw = fw;
                        sh = fh;
                        if (img.naturalHeight > img.naturalWidth) {
                            sy = Math.min(img.naturalHeight - fh, frame * fh);
                        } else {
                            sx = Math.min(img.naturalWidth - fw, frame * fw);
                        }
                    } else if (sprite.style.backgroundPosition) {
                        // 3) Tính toán từ CSS backgroundPosition nếu có
                        const bpMatch = sprite.style.backgroundPosition.match(/-?(\d+)px\s+-?(\d+)px/);
                        if (bpMatch) {
                            sx = Number(bpMatch[1]) || 0;
                            sy = Number(bpMatch[2]) || 0;
                            sw = Math.min(img.naturalWidth - sx, Math.round(sRect.width));
                            sh = Math.min(img.naturalHeight - sy, Math.round(sRect.height));
                        }
                    }
                }

                this.ctx.save();
                this.ctx.globalAlpha = Math.min(1, Math.max(0, alpha));
                this.ctx.drawImage(
                    img,
                    sx, sy, sw, sh,
                    destX,
                    destY,
                    destW,
                    destH
                );
                this.ctx.restore();
            }
        } catch {
            // Tránh crash render loop nếu DOM có thay đổi
        }
    }

    /**
     * Tự động quét và vẽ các nhãn văn bản hotspot (Callout và Titles) lên recording canvas
     * Hoàn toàn độc lập với KrPano DOM, đảm bảo chữ trong video 1080p sắc nét và không thay đổi hotspot gốc
     */
    private drawHotspotOverlays(
        w: number,
        h: number,
        krpanoCanvas: HTMLCanvasElement,
        offsetX: number,
        offsetY: number,
        drawW: number,
        drawH: number
    ): void {
        if (!this.ctx || typeof window === 'undefined') return;
        const krpano = (window as any).krpano;
        if (!krpano || typeof krpano.get !== 'function') return;

        const kRect = krpanoCanvas.getBoundingClientRect();
        if (kRect.width <= 0 || kRect.height <= 0) return;

        const scaleX = drawW / kRect.width;
        const scaleY = drawH / kRect.height;

        try {
            const count = Number(krpano.get('hotspot.count') || 0);
            if (!count || count <= 0) return;

            for (let i = 0; i < count; i++) {
                const name = krpano.get(`hotspot[${i}].name`);
                if (!name || typeof name !== 'string') continue;

                // Chỉ quét các hotspot nhãn văn bản / callout title
                const isCalloutText = name.endsWith('_callout_text');
                const isInvisible = name.endsWith('_invisible');
                if (!isCalloutText && !isInvisible) continue;

                const visible = krpano.get(`hotspot[${name}].visible`);
                const alpha = Number(krpano.get(`hotspot[${name}].alpha`) ?? 1);
                if (visible === false || visible === 'false' || alpha <= 0.05) continue;

                // Kiểm tra trường nhìn FOV
                const ath = Number(krpano.get(`hotspot[${name}].ath`));
                const atv = Number(krpano.get(`hotspot[${name}].atv`));
                if (Number.isFinite(ath)) {
                    const hlookat = Number(krpano.get('view.hlookat') || 0);
                    const deltaH = Math.abs(((ath - hlookat + 540) % 360) - 180);
                    if (deltaH > 70) continue;
                }
                if (Number.isFinite(atv)) {
                    const vlookat = Number(krpano.get('view.vlookat') || 0);
                    const deltaV = Math.abs(atv - vlookat);
                    if (deltaV > 65) continue;
                }

                if (typeof krpano.spheretoscreen === 'function' && Number.isFinite(ath) && Number.isFinite(atv)) {
                    try {
                        const sp = krpano.spheretoscreen(ath, atv);
                        if (sp && Number.isFinite(sp.x) && Number.isFinite(sp.y)) {
                            if (sp.x < -60 || sp.x > kRect.width + 60 || sp.y < -60 || sp.y > kRect.height + 60) {
                                continue;
                            }
                        }
                    } catch {}
                }

                const sprite = krpano.get(`hotspot[${name}].sprite`) as HTMLElement | null;
                if (!sprite || !sprite.isConnected) continue;

                if (sprite.style.display === 'none' || sprite.style.visibility === 'hidden') continue;

                const sRect = sprite.getBoundingClientRect();
                if (sRect.width <= 0 || sRect.height <= 0) continue;
                if (sRect.width > kRect.width * 0.6 || sRect.height > kRect.height * 0.3) continue;

                const centerX = sRect.left + sRect.width / 2;
                const centerY = sRect.top + sRect.height / 2;
                if (
                    centerX < kRect.left - sRect.width * 0.3 ||
                    centerX > kRect.right + sRect.width * 0.3 ||
                    centerY < kRect.top - sRect.height * 0.3 ||
                    centerY > kRect.bottom + sRect.height * 0.3
                ) {
                    continue;
                }
                const rawText = (sprite.innerText || krpano.get(`hotspot[${name}].html`) || '').trim();
                if (!rawText) continue;

                // Tính toán vị trí tương ứng trên 1080p canvas
                const destX = Math.round(offsetX + (sRect.left - kRect.left) * scaleX);
                const destY = Math.round(offsetY + (sRect.top - kRect.top) * scaleY);
                const destW = Math.round(sRect.width * scaleX);
                const destH = Math.round(sRect.height * scaleY);

                const computed = window.getComputedStyle(sprite);
                const bgColor = computed.backgroundColor && computed.backgroundColor !== 'rgba(0, 0, 0, 0)'
                    ? computed.backgroundColor
                    : '#FFFFFF';
                let textColor = computed.color && computed.color !== 'rgba(0, 0, 0, 0)'
                    ? computed.color
                    : '#000000';

                // Tự động bảo vệ độ tương phản cao: nếu chữ trắng trên nền trắng thì đổi sang chữ đậm dễ đọc
                const isWhiteText = textColor.includes('255, 255, 255') || textColor.toLowerCase() === '#ffffff';
                const isWhiteBg = bgColor.includes('255, 255, 255') || bgColor.toLowerCase() === '#ffffff' || bgColor === 'rgba(0, 0, 0, 0)';
                if (isWhiteText && isWhiteBg) {
                    textColor = '#0f172a';
                }

                const borderColor = computed.borderColor && computed.borderColor !== 'rgba(0, 0, 0, 0)'
                    ? computed.borderColor
                    : '#F6E493';

                this.ctx.save();
                this.ctx.globalAlpha = Math.min(1, Math.max(0, alpha));

                // 1. Vẽ nền pill bo góc và viền
                this.ctx.fillStyle = bgColor;
                this.ctx.strokeStyle = borderColor;
                this.ctx.lineWidth = Math.max(1.5, Math.round(1.5 * (w / 1920)));

                const radius = Math.min(destH / 2, Math.round(8 * (w / 1920)));
                this.ctx.beginPath();
                if (typeof this.ctx.roundRect === 'function') {
                    this.ctx.roundRect(destX, destY, destW, destH, radius);
                } else {
                    this.ctx.rect(destX, destY, destW, destH);
                }
                this.ctx.fill();
                this.ctx.stroke();

                // 2. Vẽ chữ căn giữa hoàn hảo
                this.ctx.fillStyle = textColor;
                const fontSize = Math.max(12, Math.round(13 * (w / 1920)));
                this.ctx.font = `bold ${fontSize}px "Plus Jakarta Sans", system-ui, -apple-system, sans-serif`;
                this.ctx.textAlign = 'center';
                this.ctx.textBaseline = 'middle';

                const lines = rawText.split(/<br\s*\/?>|\r?\n/gi).map((l: string) => l.trim()).filter(Boolean);
                const safeLines = lines.length > 0 ? lines : [rawText];
                const lineHeight = fontSize * 1.3;
                const startY = destY + (destH - (safeLines.length - 1) * lineHeight) / 2;

                safeLines.forEach((line: string, idx: number) => {
                    this.ctx!.fillText(line, destX + destW / 2, startY + idx * lineHeight, destW - 8);
                });

                this.ctx.restore();
            }
        } catch {
            // Tránh crash render loop nếu DOM có thay đổi
        }
    }

    /**
     * Tự động phát hiện và vẽ modal/card tài nguyên (TourAssetCornerPreview) kèm hiệu ứng xuất hiện (entrance animation)
     * lên recording canvas 1080p, đồng bộ hoàn hảo theo vị trí và kích thước trên màn hình thực tế.
     * Hỗ trợ đầy đủ: Image, Video, 3D Model, PDF Document cùng hiệu ứng: slide_from_left, slide_from_right, slide_from_top, slide_from_bottom, zoom_in.
     */
    /**
     * Kiểm tra xem nguồn ảnh / video / canvas có sạch CORS (origin-clean) hay không.
     * TUYỆT ĐỐI KHÔNG BAO GIỜ vẽ bất kỳ phần tử cross-origin không có CORS lên this.offscreenCanvas
     * vì nếu offscreenCanvas bị nhiễm bẩn (tainted), Chromium sẽ lập tức ngừng phát frame từ canvas.captureStream(),
     * làm video recording bị đứng hình (freeze frame) vĩnh viễn và toBlob() bị quăng lỗi SecurityError!
     */
    private isCleanImageSource(source: CanvasImageSource): boolean {
        if (!source) return false;
        if (typeof document === 'undefined') return true;

        if (this.cleanSourceCache.has(source as object)) {
            return this.cleanSourceCache.get(source as object) ?? false;
        }

        if (!this.testProbeCanvas) {
            this.testProbeCanvas = document.createElement('canvas');
            this.testProbeCanvas.width = 1;
            this.testProbeCanvas.height = 1;
        }
        if (!this.testProbeCtx) {
            this.testProbeCtx = this.testProbeCanvas.getContext('2d', { willReadFrequently: true });
        }

        if (!this.testProbeCtx) return true;

        try {
            this.testProbeCtx.clearRect(0, 0, 1, 1);
            this.testProbeCtx.drawImage(source, 0, 0, 1, 1);
            if (typeof this.testProbeCtx.getImageData === 'function') {
                this.testProbeCtx.getImageData(0, 0, 1, 1);
            }
            this.cleanSourceCache.set(source as object, true);
            return true;
        } catch {
            this.cleanSourceCache.set(source as object, false);
            // Hủy bỏ test canvas đã bị tainted để không làm ảnh hưởng các lần kiểm tra kế tiếp
            this.testProbeCanvas = null;
            this.testProbeCtx = null;
            return false;
        }
    }

    /**
     * Nạp phiên bản hình ảnh an toàn có CORS (hoặc qua same-origin proxy /api/proxy-image)
     * để vẽ lên recording canvas mà không làm bẩn canvas 1080p.
     */
    private getCorsSafeAssetImage(url: string): HTMLImageElement | null {
        if (!url) return null;
        if (this.assetCorsImageCache.has(url)) {
            const cached = this.assetCorsImageCache.get(url) ?? null;
            if (cached && cached.complete && cached.naturalWidth > 0) {
                return this.isCleanImageSource(cached) ? cached : null;
            }
            return null;
        }

        const img = new Image();
        img.crossOrigin = 'anonymous';

        // Nếu là ảnh từ CMC S3 hoặc external CDN, ưu tiên định tuyến qua same-origin proxy
        let targetSrc = url;
        if (
            typeof window !== 'undefined' &&
            window.location?.origin &&
            (url.includes('s3.hcm-1.cloud.cmctelecom.vn') || url.startsWith('http://') || url.startsWith('https://')) &&
            !url.startsWith(window.location.origin) &&
            !url.includes('/api/proxy-image')
        ) {
            targetSrc = `/api/proxy-image?url=${encodeURIComponent(url)}`;
        } else if (!targetSrc.includes('?')) {
            targetSrc = `${targetSrc}?_cors=1`;
        }

        img.onload = () => {
            if (this.isCleanImageSource(img)) {
                this.assetCorsImageCache.set(url, img);
            } else {
                this.assetCorsImageCache.set(url, null);
            }
        };

        img.onerror = () => {
            if (targetSrc.includes('/api/proxy-image')) {
                const fallbackImg = new Image();
                fallbackImg.crossOrigin = 'anonymous';
                fallbackImg.onload = () => {
                    if (this.isCleanImageSource(fallbackImg)) {
                        this.assetCorsImageCache.set(url, fallbackImg);
                    } else {
                        this.assetCorsImageCache.set(url, null);
                    }
                };
                fallbackImg.onerror = () => this.assetCorsImageCache.set(url, null);
                fallbackImg.src = url.includes('?') ? `${url}&_cors=1` : `${url}?_cors=1`;
                this.assetCorsImageCache.set(url, fallbackImg);
            } else {
                this.assetCorsImageCache.set(url, null);
            }
        };

        img.src = targetSrc;
        this.assetCorsImageCache.set(url, img);
        return null;
    }

    private drawTourAssetPreview(
        w: number,
        h: number,
        krpanoCanvas: HTMLCanvasElement
    ): void {
        if (!this.ctx || typeof document === 'undefined') return;

        // 1. Tìm container tài nguyên trên DOM
        const previewEl = document.querySelector<HTMLElement>('[data-tour-asset-preview="true"]');
        if (!previewEl || !previewEl.isConnected) return;

        // Bỏ qua nếu đang bị ẩn (display: none, visibility: hidden)
        const computedPreviewStyle = window.getComputedStyle(previewEl);
        if (computedPreviewStyle.display === 'none' || computedPreviewStyle.visibility === 'hidden') {
            return;
        }

        const kRect = krpanoCanvas.getBoundingClientRect();
        if (kRect.width <= 0 || kRect.height <= 0) return;

        const scaleX = w / kRect.width;
        const scaleY = h / kRect.height;

        // 2. Tìm container con mang animation (nếu có) để lấy chính xác transform và opacity
        const innerEl = previewEl.querySelector<HTMLElement>('[data-tour-asset-inner="true"]') || previewEl;
        const innerStyle = window.getComputedStyle(innerEl);
        const opacity = parseFloat(innerStyle.opacity ?? '1');
        if (isNaN(opacity) || opacity <= 0.01) return;

        // getBoundingClientRect() của innerEl đã bao gồm toàn bộ transform (scale, translate3d...) của CSS entrance animation!
        const aRect = innerEl.getBoundingClientRect();
        if (aRect.width <= 0 || aRect.height <= 0) return;

        // Kiểm tra xem preview có đang nằm trong viewport hay không
        if (
            aRect.right < kRect.left ||
            aRect.left > kRect.right ||
            aRect.bottom < kRect.top ||
            aRect.top > kRect.bottom
        ) {
            return;
        }

        const drawX = Math.round((aRect.left - kRect.left) * scaleX);
        const drawY = Math.round((aRect.top - kRect.top) * scaleY);
        const drawW = Math.round(aRect.width * scaleX);
        const drawH = Math.round(aRect.height * scaleY);

        this.ctx.save();
        this.ctx.globalAlpha = Math.min(1, Math.max(0, opacity));

        try {
            // 3. Nếu là dạng card đầy đủ (không phải minimal): vẽ viền khung, bóng đổ
            const isMinimal = previewEl.getAttribute('data-tour-asset-minimal') === 'true'
                || previewEl.classList.contains('overflow-visible');

            if (!isMinimal) {
                const cornerRadius = Math.round(12 * (w / 1920));
                this.ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
                this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
                this.ctx.lineWidth = Math.max(1, Math.round(1.5 * (w / 1920)));

                this.ctx.beginPath();
                if (typeof this.ctx.roundRect === 'function') {
                    this.ctx.roundRect(drawX, drawY, drawW, drawH, cornerRadius);
                } else {
                    this.ctx.rect(drawX, drawY, drawW, drawH);
                }
                this.ctx.fill();
                this.ctx.stroke();
            }

            // 4. Tìm và vẽ nội dung media (Image, Video, hoặc Canvas từ 3D/PDF)
            const contentEl = innerEl.querySelector<HTMLElement>('.aspect-square') || innerEl;
            const cRect = contentEl.getBoundingClientRect();
            const cX = Math.round((cRect.left - kRect.left) * scaleX);
            const cY = Math.round((cRect.top - kRect.top) * scaleY);
            const cW = Math.round(cRect.width * scaleX);
            const cH = Math.round(cRect.height * scaleY);

            const imgEl = contentEl.querySelector('img') as HTMLImageElement | null;
            const videoEl = contentEl.querySelector('video') as HTMLVideoElement | null;
            const canvasEl = contentEl.querySelector('canvas') as HTMLCanvasElement | null;

            if (imgEl && imgEl.complete && imgEl.naturalWidth > 0 && imgEl.naturalHeight > 0) {
                // Xác định hình ảnh sạch CORS để vẽ lên canvas (tuyệt đối không để làm bẩn offscreenCanvas)
                let sourceToDraw: CanvasImageSource | null = null;
                if (this.isCleanImageSource(imgEl)) {
                    sourceToDraw = imgEl;
                } else {
                    sourceToDraw = this.getCorsSafeAssetImage(imgEl.src);
                }

                if (sourceToDraw) {
                    const natW = (sourceToDraw as any).naturalWidth || (sourceToDraw as any).videoWidth || (sourceToDraw as any).width || 1;
                    const natH = (sourceToDraw as any).naturalHeight || (sourceToDraw as any).videoHeight || (sourceToDraw as any).height || 1;
                    const imgAspect = natW / (natH || 1);
                    const containerAspect = cW / (cH || 1);
                    let fitW = cW;
                    let fitH = cH;
                    let fitX = cX;
                    let fitY = cY;

                    if (imgAspect > containerAspect) {
                        fitH = Math.round(cW / imgAspect);
                        fitY = Math.round(cY + (cH - fitH) / 2);
                    } else {
                        fitW = Math.round(cH * imgAspect);
                        fitX = Math.round(cX + (cW - fitW) / 2);
                    }

                    try {
                        this.ctx.drawImage(sourceToDraw, fitX, fitY, fitW, fitH);
                    } catch {
                        // Bỏ qua lỗi vẽ để không ảnh hưởng render loop
                    }
                } else {
                    // Trong khi ảnh CORS an toàn đang nạp, vẽ placeholder card mờ tinh tế
                    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
                    this.ctx.fillRect(cX, cY, cW, cH);
                }
            } else if (videoEl && videoEl.readyState >= 2 && videoEl.videoWidth > 0 && videoEl.videoHeight > 0) {
                // Chỉ vẽ video nếu video sạch nguồn gốc
                if (this.isCleanImageSource(videoEl)) {
                    const vidAspect = videoEl.videoWidth / videoEl.videoHeight;
                    const containerAspect = cW / (cH || 1);
                    let fitW = cW;
                    let fitH = cH;
                    let fitX = cX;
                    let fitY = cY;

                    if (vidAspect > containerAspect) {
                        fitH = Math.round(cW / vidAspect);
                        fitY = Math.round(cY + (cH - fitH) / 2);
                    } else {
                        fitW = Math.round(cH * vidAspect);
                        fitX = Math.round(cX + (cW - fitW) / 2);
                    }

                    try {
                        this.ctx.drawImage(videoEl, fitX, fitY, fitW, fitH);
                    } catch {
                        // Ignore video draw error
                    }
                }
            } else if (canvasEl && canvasEl.width > 0 && canvasEl.height > 0) {
                // Chỉ vẽ 3D Model hoặc PDF Canvas nếu canvas con sạch nguồn gốc
                if (this.isCleanImageSource(canvasEl)) {
                    try {
                        this.ctx.drawImage(canvasEl, cX, cY, cW, cH);
                    } catch {
                        // Ignore canvas draw error
                    }
                }
            }

            // 5. Vẽ nút đóng (X) nếu có hiển thị trên DOM
            const closeBtn = innerEl.querySelector<HTMLButtonElement>('[data-tour-asset-close-btn="true"], button')
                || innerEl.querySelector<HTMLButtonElement>('button');

            if (closeBtn && isMinimal) {
                const bRect = closeBtn.getBoundingClientRect();
                if (bRect.width > 0 && bRect.height > 0) {
                    const bX = Math.round((bRect.left - kRect.left) * scaleX);
                    const bY = Math.round((bRect.top - kRect.top) * scaleY);
                    const bW = Math.round(bRect.width * scaleX);
                    const radius = bW / 2;
                    const centerX = bX + radius;
                    const centerY = bY + radius;

                    // Vẽ nền tròn mờ
                    this.ctx.beginPath();
                    this.ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
                    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
                    this.ctx.fill();
                    this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.75)';
                    this.ctx.lineWidth = Math.max(1, Math.round(1.5 * (w / 1920)));
                    this.ctx.stroke();

                    // Vẽ chữ X
                    const xOffset = radius * 0.4;
                    this.ctx.beginPath();
                    this.ctx.moveTo(centerX - xOffset, centerY - xOffset);
                    this.ctx.lineTo(centerX + xOffset, centerY + xOffset);
                    this.ctx.moveTo(centerX + xOffset, centerY - xOffset);
                    this.ctx.lineTo(centerX - xOffset, centerY + xOffset);
                    this.ctx.strokeStyle = '#1e293b'; // slate-800
                    this.ctx.lineWidth = Math.max(1.5, Math.round(2.2 * (w / 1920)));
                    this.ctx.lineCap = 'round';
                    this.ctx.stroke();
                }
            }

            // 6. Vẽ thanh tiêu đề bên dưới (đối với non-minimal preview)
            if (!isMinimal) {
                const titleBar = innerEl.querySelector<HTMLElement>('.border-t');
                if (titleBar) {
                    const tbRect = titleBar.getBoundingClientRect();
                    const tbX = Math.round((tbRect.left - kRect.left) * scaleX);
                    const tbY = Math.round((tbRect.top - kRect.top) * scaleY);
                    const tbW = Math.round(tbRect.width * scaleX);
                    const tbH = Math.round(tbRect.height * scaleY);

                    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
                    this.ctx.fillRect(tbX, tbY, tbW, tbH);

                    this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
                    this.ctx.lineWidth = 1;
                    this.ctx.beginPath();
                    this.ctx.moveTo(tbX, tbY);
                    this.ctx.lineTo(tbX + tbW, tbY);
                    this.ctx.stroke();

                    const titleTextEl = titleBar.querySelector<HTMLElement>('button, div');
                    const titleText = (titleTextEl?.textContent || titleTextEl?.getAttribute('title') || '').trim();
                    if (titleText) {
                        const fontSize = Math.max(12, Math.round(14 * (w / 1920)));
                        this.ctx.font = `500 ${fontSize}px "Plus Jakarta Sans", system-ui, -apple-system, sans-serif`;
                        this.ctx.fillStyle = '#FFFFFF';
                        this.ctx.textAlign = 'left';
                        this.ctx.textBaseline = 'middle';
                        this.ctx.fillText(titleText, tbX + Math.round(12 * (w / 1920)), tbY + tbH / 2, tbW - Math.round(60 * (w / 1920)));
                    }
                }
            }
        } catch {
            // Tránh crash render loop nếu DOM đang unmount
        } finally {
            this.ctx.restore();
        }
    }

    /**
     * Dừng ghi và trả về video Blob cùng metadata
     * @param silentOnError Nếu true sẽ không kích hoạt onError callback khi thời lượng quá ngắn (<5s)
     */
    public async stopRecording(silentOnError: boolean = false): Promise<RecordingResult | null> {
        if (this.status !== 'recording' || !this.mediaRecorder) {
            return null;
        }

        this.updateStatus('stopping');

        if (this.timerInterval) {
            clearInterval(this.timerInterval);
            this.timerInterval = null;
        }

        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }

        if (this.keepAliveOscillator) {
            try {
                this.keepAliveOscillator.stop();
                this.keepAliveOscillator.disconnect();
            } catch {}
            this.keepAliveOscillator = null;
        }

        const duration = Math.max(
            this.elapsedTime,
            this.startTime > 0 ? Math.floor((Date.now() - this.startTime) / 1000) : 0
        );
        const tourName = this.config.tourName || 'Tour Recording';
        const idPath = this.config.idPath || (typeof window !== 'undefined' ? (window as any).id_path || '' : '');

        // Trích xuất ảnh đại diện sắc nét trực tiếp từ canvas 1080p trước khi dọn dẹp bộ nhớ
        let thumbnailBlob: Blob | null = null;
        if (this.offscreenCanvas && typeof this.offscreenCanvas.toBlob === 'function') {
            try {
                thumbnailBlob = await new Promise<Blob | null>((res) => {
                    this.offscreenCanvas!.toBlob((b) => res(b), 'image/jpeg', 0.85);
                });
            } catch {
                thumbnailBlob = null;
            }
        }

        return new Promise((resolve) => {
            if (!this.mediaRecorder) {
                this.cleanup();
                return resolve(null);
            }

            const stopTimeout = setTimeout(() => {
                if (this.status === 'stopping') {
                    console.warn('[TourRecorder] onstop timeout reached, forcing cleanup');
                    this.cleanup();
                    resolve(null);
                }
            }, 5000);

            this.mediaRecorder.onstop = () => {
                clearTimeout(stopTimeout);
                const mimeType = getBestSupportedMimeType() || 'video/mp4';
                const blob = new Blob(this.recordedChunks, { type: mimeType });
                const fileSize = blob.size;

                console.log(`[TourRecorder] onstop: duration=${duration}s, fileSize=${fileSize} bytes, chunks=${this.recordedChunks.length}`);

                this.cleanup();

                // 1. Bỏ qua nếu thời gian ghi dưới 5 giây
                if (duration < 5) {
                    if (!silentOnError) {
                        this.config.onError?.('Thời lượng ghi hình quá ngắn (dưới 5 giây). Video không được lưu.');
                    }
                    return resolve(null);
                }

                // 2. Bỏ qua nếu dữ liệu rỗng
                if (fileSize < 1000) {
                    if (!silentOnError) {
                        this.config.onError?.(`Dữ liệu video bị rỗng hoặc không ghi nhận được khung hình (${fileSize} bytes).`);
                    }
                    return resolve(null);
                }

                this.updateStatus('completed');
                resolve({
                    blob,
                    duration,
                    tourName,
                    idPath,
                    fileSize,
                    thumbnailBlob
                });
            };

            try {
                if (this.mediaRecorder.state === 'recording') {
                    try {
                        this.mediaRecorder.requestData();
                    } catch {}
                    this.mediaRecorder.stop();
                } else {
                    this.cleanup();
                    resolve(null);
                }
            } catch (err) {
                console.error('[TourRecorder] Lỗi khi dừng MediaRecorder:', err);
                this.cleanup();
                resolve(null);
            }
        });
    }

    private cleanup(): void {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
            this.timerInterval = null;
        }
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        if (this.keepAliveOscillator) {
            try {
                this.keepAliveOscillator.stop();
                this.keepAliveOscillator.disconnect();
            } catch {}
            this.keepAliveOscillator = null;
        }
        // Hủy và ngắt kết nối an toàn các audio bridge
        for (const bridge of this.activeBridges.values()) {
            try {
                bridge.localSource?.disconnect();
                bridge.foreignDest?.disconnect();
            } catch {}
        }
        this.activeBridges.clear();
        for (const srcNode of this.streamSourceNodes.values()) {
            try { srcNode.disconnect(); } catch {}
        }
        this.streamSourceNodes.clear();
        this.externalMediaStreams.clear();
        this.activeMicTrackIds.clear();
        for (const el of Array.from(this.externalMediaElements)) {
            if (!el || el.ended || el.paused || (typeof document !== 'undefined' && !document.body?.contains(el))) {
                this.externalMediaElements.delete(el);
            }
        }

        this.cachedKrpanoCanvas = null;
        this.cachedAvatarCanvas = null;
        this.hotspotIconCache.clear();
        this.svgIconCache.clear();
        this.assetCorsImageCache.clear();
        this.testProbeCanvas = null;
        this.testProbeCtx = null;
        this.updateStatus('idle');
    }
}

export const tourRecorder = TourRecorder.getInstance();
if (typeof window !== 'undefined') {
    (window as any).tourRecorder = tourRecorder;
}
